import _, {without} from 'lodash';
import {batch} from 'react-redux';
import uuid from 'uuid-random';

import {logger} from '../../../../srcCommon/services/logger/logger';
import {RUpdateConversationsSeenTask} from '../../../../srcCommon/tasks/conversations/updateConversationsSeenTask';
import {createTask} from '../../../../srcCommon/tasks/task';
import {
  findConversationNewReadDate,
  findConversationSeenAtEverything,
  findLastActivityDate,
  findTracker,
  findUnreadStatus,
  isConversationInTeamContext,
  UnreadStatusesEnum,
} from '../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {ConversationTrackerFlagsEnum} from '../../../models/api/conversationTrackerModel';
import {AppState} from '../../appState';
import {StateGetter} from '../../appStore';
import {ThunkAsyncAction} from '../../appThunk';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {InboxesParameters} from '../../panels/inboxes/parameters/inboxesParameters';
import {getConversations, getOriginalConversations} from '../dataSelectors';
import {updateConversationsBulkAction} from './bulkConversationsActions';
import {conversationMarkedAsSeenAction} from './conversationsActions';
import {updateConversationsAction} from './updateConversationsActions';

interface ToggleMarkAsSeenOptions {
  forceUpdate?: boolean;
  cellsContext: CellsContext;
  seenAt?: number;
}

export function toggleMarkConversationAsSeenAction(
  conversationIds: ReadonlyArray<number>,
  isSeen: boolean,
  options: ToggleMarkAsSeenOptions,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const allConversations = getConversations(state);
    const conversations = _(conversationIds)
      .map((id) => allConversations[id])
      .compact()
      .value();

    const {forceUpdate, seenAt, cellsContext} = options;
    const {teammateId} = cellsContext;

    // Check if any of the specified conversations are unread.
    const hasUnreadConversation = conversations.some(
      (c) => findUnreadStatus(cellsContext, c) !== UnreadStatusesEnum.NONE,
    );

    // If some conversations are not in store (e.g. conversation search results), we still want the action to go through.
    const areAllConversationsInStore = conversations.length === conversationIds.length;

    // If we have no unread conversations *and* we want to mark as read, nothing to do.
    // TODO: Do we really want this, and if so, should the backend prevent moving auto_seen_at if there are have been no
    // new activities since the last auto_seen_at?
    if (areAllConversationsInStore && !hasUnreadConversation && isSeen && !forceUpdate) {
      return;
    }

    if (!isSeen && seenAt) {
      batch(() => {
        conversationIds.forEach((id) => {
          dispatch(conversationMarkedAsSeenAction(id, seenAt));
        });
      });
    }

    const offlineTask = maybeCreateOfflineTask(
      getState,
      options.cellsContext,
      conversations.map((conversation) => ({
        conversationId: conversation.id,
        seenAt: isSeen ? findConversationSeenAtEverything(conversation, teammateId) : options.seenAt || 0,
      })),
    );

    // Temporary instrumentation for https://frontjira.atlassian.net/browse/PB-19811
    const isSimpleMarkAsRead = isSeen && !options.seenAt;
    const unreadConversationRecords = isSimpleMarkAsRead
      ? selectUnreadConversationRecords(getState(), cellsContext, conversationIds)
      : undefined;

    // If so, mark it as read.
    await dispatch(
      updateConversationsAction(
        conversationIds,
        buildSeenConversationPatch(isSeen, seenAt),
        (conversation) => {
          const newSeenAtCandidate = findConversationNewReadDate(conversation, teammateId);
          const newSeenAt = isSeen ? newSeenAtCandidate : seenAt || 0;
          return {
            ...conversation,
            seenAt: newSeenAt,
            ...clearUnsnoozeFlag(isSeen, conversation, cellsContext),
          };
        },
        {offlineTask},
      ),
    );

    // Temporary instrumentation for https://frontjira.atlassian.net/browse/PB-19811
    if (unreadConversationRecords) {
      logConversationsStillUnread(getState(), cellsContext, unreadConversationRecords);
    }
  };
}

export function toggleBulkConversationAsSeenBulkAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  isSeen: boolean,
  seenAt?: number,
): ThunkAsyncAction {
  const conversationPatch = buildSeenConversationPatch(isSeen, seenAt);
  return updateConversationsBulkAction(inboxesParameters, cellsContext, conversationPatch);
}

function buildSeenConversationPatch(isSeen: boolean, seenAt: number | undefined) {
  // Updating both seenAt and isSeen at the same time causes conflicts.
  return {
    isSeen: seenAt ? undefined : isSeen,
    seenAt,
  };
}

function selectUnreadConversationRecords(
  state: AppState,
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
) {
  const originalConversations = getOriginalConversations(state);
  const optimisticConversations = getConversations(state);

  return _(conversationIds)
    .map((id) => originalConversations[id])
    .compact()
    .filter((conversation) => findUnreadStatus(cellsContext, conversation) !== UnreadStatusesEnum.NONE)
    .compact()
    .map((conversation) => [conversation, optimisticConversations[conversation.id]] as const)
    .map(([conversation, optimisticConversation]) => ({
      id: conversation.id,
      seen_at: conversation.seenAt ?? -1,
      last_activity_date: findLastActivityDate(conversation, cellsContext.teammateId),
      updated_at: conversation.updatedAt,
      optimistic_seen_at: optimisticConversation?.seenAt ?? -1,
      optimistic_updated_at: optimisticConversation?.updatedAt ?? -1,
    }))
    .value();
}

function logConversationsStillUnread(
  state: AppState,
  cellsContext: CellsContext,
  unreadConversationRecords: ReturnType<typeof selectUnreadConversationRecords>,
) {
  unreadConversationRecords.forEach((record) => {
    const nextConversation = getOriginalConversations(state)[record.id];
    if (!nextConversation) {
      return;
    }

    const unreadStatus = findUnreadStatus(cellsContext, nextConversation);
    if (unreadStatus === UnreadStatusesEnum.NONE) {
      return;
    }

    const nextOptimisticConversation = getConversations(state)[record.id];
    logger.warn('Conversation still unread after marking as read', {
      cells_context_type: cellsContext.type,
      teammate_id: cellsContext.teammateId,
      conversation: {
        ...record,
        next_seen_at: nextConversation.seenAt ?? -1,
        next_last_activity_date: findLastActivityDate(nextConversation, cellsContext.teammateId),
        next_updated_at: nextConversation.updatedAt,
        next_optimistic_seen_at: nextOptimisticConversation?.seenAt ?? -1,
        next_optimistic_updated_at: nextOptimisticConversation?.updatedAt ?? -1,
      },
    });
  });
}

function clearUnsnoozeFlag(isSeen: boolean, conversation: ConversationBaseModel, cellsContext: CellsContext) {
  if (!isSeen) {
    return {};
  }

  // Team context: clear global unsnoozed flag from conversation.
  if (isConversationInTeamContext(cellsContext, conversation)) {
    return {isUnsnoozed: false};
  }

  const {teammateId} = cellsContext;
  const tracker = findTracker(conversation, teammateId);
  if (!tracker) {
    return {};
  }

  // Teammate context: clear unsnoozed flag from tracker.
  const newTracker = {...tracker, extra: without(tracker.extra, ConversationTrackerFlagsEnum.UNSNOOZED)};

  return {
    trackers: {
      ...conversation.trackers,
      [teammateId]: newTracker,
    },
  };
}

function maybeCreateOfflineTask(
  getState: StateGetter,
  cellsContext: CellsContext,
  updates: ReadonlyArray<{conversationId: number; seenAt: number}>,
) {
  const state = getState();
  const {teammateId} = cellsContext;
  const allConversations = getConversations(state);

  const conversations = _(updates)
    .map(({conversationId: id, seenAt}) => {
      const conversation = allConversations[id];
      if (!conversation) {
        return undefined;
      }

      // Use the optimistic state from any previously scheduled tasks.
      return {
        id,
        seenAt,
        lastSeenUpdatedAt: conversation.seenUpdatedAt,
        lastActivityAt: conversation.lastActivityAt,
        offlineSeenId: conversation.offlineSeenId || uuid(),
      };
    })
    .compact()
    .value();
  if (conversations.length === 0) {
    return undefined;
  }

  return createTask(RUpdateConversationsSeenTask, {
    conversations,
    teammateId,
    initiatedAt: Date.now(),
  });
}
